// Filter bar — status, siteType, and date-range dropdowns for hosted sites list

import { cn } from '../../lib/utils'

export type DateRange = 'all' | '7d' | '30d' | '90d'

export interface FilterBarValues {
  status: string
  siteType: string
  dateRange: DateRange
}

interface FilterBarProps {
  status: string
  siteType: string
  dateRange: DateRange
  onChange: (filters: FilterBarValues) => void
  isDark: boolean
}

const SELECT_BASE =
  'rounded-lg border py-1.5 pl-2.5 pr-6 text-xs outline-none appearance-none bg-no-repeat cursor-pointer'

function selectCls(isDark: boolean) {
  return cn(
    SELECT_BASE,
    isDark
      ? 'border-white/[0.06] bg-surface-2 text-neutral-300 focus:border-brand-500/50'
      : 'border-neutral-200 bg-neutral-50 text-neutral-700 focus:border-brand-500',
  )
}

export function FilterBar({ status, siteType, dateRange, onChange, isDark }: FilterBarProps) {
  const isFiltered = status !== '' || siteType !== '' || dateRange !== 'all'

  function update(patch: Partial<FilterBarValues>) {
    onChange({ status, siteType, dateRange, ...patch })
  }

  return (
    <div className="flex items-center gap-2">
      {/* Status filter */}
      <select
        value={status}
        onChange={(e) => update({ status: e.target.value })}
        aria-label="Filter by status"
        className={selectCls(isDark)}
      >
        <option value="">All statuses</option>
        <option value="ready">Ready</option>
        <option value="processing">Processing</option>
        <option value="failed">Failed</option>
      </select>

      {/* Type filter */}
      <select
        value={siteType}
        onChange={(e) => update({ siteType: e.target.value })}
        aria-label="Filter by site type"
        className={selectCls(isDark)}
      >
        <option value="">All types</option>
        <option value="html">HTML</option>
        <option value="zip">ZIP</option>
      </select>

      {/* Date range filter */}
      <select
        value={dateRange}
        onChange={(e) => update({ dateRange: e.target.value as DateRange })}
        aria-label="Filter by date range"
        className={selectCls(isDark)}
      >
        <option value="all">All time</option>
        <option value="7d">Last 7 days</option>
        <option value="30d">Last 30 days</option>
        <option value="90d">Last 90 days</option>
      </select>

      {/* Reset button — only visible when a non-default filter is active */}
      {isFiltered && (
        <button
          type="button"
          onClick={() => onChange({ status: '', siteType: '', dateRange: 'all' })}
          className={cn(
            'rounded-lg border px-2.5 py-1.5 text-xs transition-colors',
            isDark
              ? 'border-white/[0.06] text-neutral-400 hover:border-white/[0.12] hover:text-neutral-200'
              : 'border-neutral-200 text-neutral-500 hover:border-neutral-300 hover:text-neutral-700',
          )}
        >
          Reset filters
        </button>
      )}
    </div>
  )
}
